import { useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import { WhatshotRounded } from '@mui/icons-material';
import { grey, red, green, amber, deepOrange, deepPurple } from '@mui/material/colors';
import { StatusButton } from '../../../Components';
import { useDataGridPagination } from '../../../../../Global/hooks';
import { DateFormatter, UserGroup } from '../../../../../Global/Components';
import { FormatDateFull } from '../../../../../Global/Functions';
import UserInputValue from '../../../../../Global/DataGrid/Filters/UserInput';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import TaskTypes from '../../../../../Global/TaskTypes';
import OptionsInput from '../../../../../Global/DataGrid/Filters/OptionsInput';
import StateManager from '../../../../../Global/StateManager';
import moment from 'moment';
import { isArray } from 'lodash';
import { priorities } from '../../../constants';

export default function TasksTable({ boxParams, columnHeaderHeight = 60, rowHeight = 50, itemId }) {
  const columns = useMemo(() => {
    const PriorityColors = {
      None: grey[400],
      Low: green[500],
      Medium: amber[500],
      High: deepOrange[500],
      Critical: deepPurple[500],
    };

    const PrioritiesOptions = priorities.map((priority) => ({
      _id: priority,
      title: priority,
      icon: <WhatshotRounded style={{ color: PriorityColors[priority] }} />,
    }));

    return [
      {
        field: 'priority',
        headerName: 'Priority',
        width: 60,
        align: 'center',
        display: 'flex',
        filterOperators: [
          {
            label: 'Is any of',
            value: 'isAnyOf',
            InputComponent: OptionsInput,
            InputComponentProps: {
              options: PrioritiesOptions,
            },
          },
        ],
        renderCell: (value, row) => (
          <Grid container item alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <WhatshotRounded
              style={{
                color: PriorityColors[value.row.priority],
                display: 'block',
                margin: 'auto',
              }}
            />
          </Grid>
        ),
      },
      {
        field: 'title',
        headerName: 'Task Title',
        display: 'flex',
        flex: 1,
        renderCell: ({ value }) => <Typography>{value}</Typography>,
      },
      {
        field: 'dueAt',
        headerName: 'Due date',
        display: 'flex',
        type: 'date',
        width: 200,
        valueGetter: (value) => (value ? moment(value).toDate() : null),
        renderCell: ({ value }) => {
          const overdue = value && moment(value) < moment();
          return (
            <Grid container item alignItems={'center'} justifyContent={'center'} sx={{ height: '100%' }}>
              <Typography
                style={{ fontSize: '0.95rem', color: overdue ? red[500] : '', fontWeight: overdue ? 500 : 400 }}
                noWrap
              >
                <DateFormatter date={value} formatFunction={FormatDateFull} />
              </Typography>
            </Grid>
          );
        },
      },
      {
        field: 'assignedUsers',
        headerName: 'Assigned',
        sortable: false,
        width: 300,
        align: 'left',
        display: 'flex',
        filterOperators: [
          {
            label: 'Includes',
            value: 'includes',
            InputComponent: UserInputValue,
            InputComponentProps: { type: 'user' },
            getValueAsString: (value) => (isArray(value) ? value.join(', ') : ''),
          },
        ],
        renderCell: ({ row }) => {
          return <UserGroup ids={row.assignedUsers} avatarSize={32} />;
        },
      },
      {
        field: 'sourceTitle',
        headerName: 'Source',
        width: 180,
        alignItems: 'left',
        display: 'flex',
        flex: 0.6,
        justifyContent: 'center',
        renderCell: ({ row }) => {
          const taskTypeInfo = TaskTypes[row.type] ?? TaskTypes.workflow;
          return (
            <Grid container item alignItems="center" wrap="nowrap">
              {taskTypeInfo.icon}
              <Typography sx={{ ml: 1 }}>{row.sourceTitle || taskTypeInfo.text}</Typography>
            </Grid>
          );
        },
      },
      {
        field: 'progress',
        headerName: 'Status',
        width: 200,
        renderCell: (value, row) => (
          <Grid
            container
            item
            alignItems={'center'}
            justifyContent={'center'}
            sx={{ height: '100%', borderRadius: 10 }}
          >
            <StatusButton
              status={value.row.progress}
              isDisabled
              isOverdue={value.row.isOverdue}
              style={{ maxHeigh: '100%', maxWidth: '100%' }}
            />
          </Grid>
        ),
      },
    ];
  }, []);

  const tableConfig = useDataGridPagination(
    boxParams ? `tasks-box-${itemId}` : null,
    columns,
    '/tasks/data/loadTaskBoxTable',
    'tasks',
    'totalCount',
    'personal',
    { requestData: { box: boxParams } },
  );

  function openTask(taskId) {
    StateManager.selectTask(taskId, updateTask, deleteTask);
  }

  function updateTask(task) {
    if (!task?._id) return;

    const copy = tableConfig.getRows();
    const index = copy.findIndex((x) => x._id === task._id);

    if (index === -1) return;
    copy[index] = { ...copy[index], ...task, type: copy[index].type };

    const notCompleted = copy.filter((x) => !x.completedDate);

    tableConfig.updateRows(notCompleted);
  }

  function deleteTask(taskId) {
    const copy = tableConfig.getRows();
    const remaining = copy.filter((x) => x._id !== taskId);
    tableConfig.updateRows(remaining);
  }

  return (
    <DataGridPremium
      columnHeaderHeight={columnHeaderHeight}
      rowHeight={rowHeight}
      disableAggregation
      disableRowGrouping
      {...tableConfig.gridProps}
      onRowClick={({ row }) => openTask(row._id)}
      slots={{
        noRowsOverlay: () => (
          <Grid
            container
            sx={{
              flex: 1,
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography>No tasks</Typography>
          </Grid>
        ),
      }}
    />
  );
}
